import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form/dist/index.ie11';
import Image from './Image';
import IncompatiblePrescription from './IncompatiblePrescription';
import GenericButton from './GenericButton';
import { useSelector, useDispatch } from 'react-redux';
import utils from '../app/utils';
import constants from '../app/constants';
import {
    setSelectedPrescriptionObject,
    setIsPrescriptionModalOpen,
    setIsPrescriptionFormOpen,
    setIsFrameOnlySelected,
} from '../app/mainSlice';
import { isMobile } from 'react-device-detect';

/**
 * @param props.config
 * @param props.isSingleType
 * @param props.handleSetSelectedType
 * @param props.brandBaseUrl
 */
const SimplePrescriptionForm = props => {
    const dispatch = useDispatch();
    const labels = useSelector(state => state.main.labels);
    const baseURLs = useSelector(state => state.main.baseURLs);
    const [compatibleFramesUrl, setCompatibleFramesUrl] = useState(null);
    const [compatibleFramesPrescriptionObject, setCompatibleFramesPrescriptionObject] = useState(
        null
    );
    const isPrescriptionFormOpen = useSelector(state => state.main.isPrescriptionFormOpen);
    const data = useSelector(state => state.main.data);
    const extPrescriptionObject = useSelector(state => state.main.extPrescriptionObject);
    const selectedPrescriptionObject = useSelector(state => state.main.selectedPrescriptionObject);
    const isPrescriptionModalOpen = useSelector(state => state.main.isPrescriptionModalOpen);

    const openPrescriptionModal = event => {
        if ((event.type === 'keydown' && event.key === 'Enter') || event.type === 'click') {
            dispatch(setIsPrescriptionModalOpen(true));
        }
    };

    const {
        register,
        errors,
        handleSubmit,
        watch,
        clearErrors,
        setError,
        reset,
        setValue,
    } = useForm();

    const sphOdVal = watch('sph_od');
    const sphOsVal = watch('sph_os');

    useEffect(() => {
        if (selectedPrescriptionObject || extPrescriptionObject) {
            if (selectedPrescriptionObject) {
                setValue('sph_od', selectedPrescriptionObject.SPH.OD);
                setValue('sph_os', selectedPrescriptionObject.SPH.OS);
                if (selectedPrescriptionObject.CYL) {
                    setValue('cyl_od', selectedPrescriptionObject.CYL.OD);
                    setValue('cyl_os', selectedPrescriptionObject.CYL.OS);
                }
                setValue('addCheck', selectedPrescriptionObject.ADD == true ? 'checked' : '');
            } else if (extPrescriptionObject) {
                setValue('sph_od', extPrescriptionObject.SPH.OD);
                setValue('sph_os', extPrescriptionObject.SPH.OS);
                if (extPrescriptionObject.CYL) {
                    setValue('cyl_od', extPrescriptionObject.CYL.OD);
                    setValue('cyl_os', extPrescriptionObject.CYL.OS);
                }
                setValue('addCheck', extPrescriptionObject.ADD == true ? 'checked' : '');
            }

            dispatch(setIsPrescriptionFormOpen(true));
        }
    }, [extPrescriptionObject, selectedPrescriptionObject]);

    const onSubmit = formData => {
        console.log(formData);

        // Serialize prescription object from form
        const prescriptionObject = utils.getSimplePrescriptionObject(formData);

        // Check if packages include prescription compatible lenses
        const compatibleLensesType = utils.getCompatibleLensesType(
            data.packages,
            prescriptionObject
        );

        if (
            compatibleLensesType !== null &&
            utils.isFramePrescriptionCompatible(
                props.config.data.frame.rxValues,
                prescriptionObject
            )
        ) {
            if (process.env.NODE_ENV === 'development') {
                alert('There are compatible lenses!');
            }

            if (props.config.prescriptionModule.savePrescription(prescriptionObject)) {
                dispatch(setSelectedPrescriptionObject(prescriptionObject));

                props.handleSetSelectedType({
                    type: compatibleLensesType,
                });
            } else {
                try {
                    tealium_data2track.push({
                        id: 'Error',
                        Error_Source: 'User',
                        Error_Code: 'RX Configurator: prescription values',
                        Error_Details:
                            'ADD:' +
                            prescriptionObject.ADD +
                            ',SPHOD:' +
                            prescriptionObject.SPH.OD +
                            ',SPHOS:' +
                            prescriptionObject.SPH.OS,
                        Error_Message:
                            'Sorry! The frame you ve chosen isn t compatible with your prescription',
                    });
                } catch (error) {
                    console.error(
                        'Error during tealium_data2track push. Check if tealium_data2track exists in the window.'
                    );
                }
                console.error('Error while saving prescription');
            }
        } else {
            try {
                tealium_data2track.push({
                    id: 'Error',
                    Error_Source: 'User',
                    Error_Code: 'RX Configurator: prescription values',
                    Error_Details:
                        'ADD:' +
                        prescriptionObject.ADD +
                        ',SPHOD:' +
                        prescriptionObject.SPH.OD +
                        ',SPHOS:' +
                        prescriptionObject.SPH.OS,
                    Error_Message:
                        'Sorry! The frame you ve chosen isn t compatible with your prescription',
                });
           } catch (error) {
                console.error(
                    'Error during tealium_data2track push. Check if tealium_data2track exists in the window.'
                );
            }

            // No prescription compatible lenses
            let compatibleFramesResult = props.config.prescriptionModule.checkAvailableFrames(
                props.config.data.frame,
                prescriptionObject
            );

            if (compatibleFramesResult instanceof Promise) {
                compatibleFramesResult.then(
                    compatibleFramesUrl => {
                        if (compatibleFramesUrl) {
                            setCompatibleFramesPrescriptionObject(prescriptionObject);
                            setCompatibleFramesUrl(compatibleFramesUrl);
                        }
                    },
                    error => {
                        console.log('Unable to get compatible frames URL');
                    }
                );
            } else {
                setCompatibleFramesPrescriptionObject(prescriptionObject);
                setCompatibleFramesUrl(compatibleFramesResult);
            }

            ['sph_od', 'sph_os'].forEach(elem =>
                setError(elem, {
                    type: 'manual',
                })
            );

            setError('rxIncompatible', {
                type: 'manual',
            });
        }
    };

    const handleClear = () => {
        clearErrors();
        reset();
        props.config.prescriptionModule.clearPrescription();
        setSelectedPrescriptionObject(null);
    };

    const handleOpenAccordion = useCallback(event => {
        if (event.key === 'Enter') {
            dispatch(setIsPrescriptionFormOpen(true));
        }
    }, []);

    return (
        <div className="lc-accordion-group prescription-container">
            <input
                className="lc-accordion"
                id="seePrescriptionForm"
                type="checkbox"
                checked={isPrescriptionFormOpen}
                onChange={() => {
                    dispatch(setIsFrameOnlySelected(false));
                    dispatch(setIsPrescriptionFormOpen(!isPrescriptionFormOpen));
                }}
            />
            <a data-element-id={!isPrescriptionFormOpen ? 'X_X_LensPanel_Type-ValuesOpen' : ''}>
                <label
                    aria-label="Open accordion with store hours"
                    className="lc-accordion"
                    htmlFor="seePrescriptionForm"
                    tabIndex={isPrescriptionModalOpen ? '-1' : '0'}
                    onKeyPress={handleOpenAccordion}
                >
                    {labels.prescription.toggleLabel}
                </label>
            </a>
            <div
                className={
                    isPrescriptionFormOpen
                        ? 'open lc-accordion prescription-form-container'
                        : 'closed lc-accordion prescription-form-container'
                }
            >
                <div className="prescription-title">{labels.prescription.prescriptionTitle}</div>
                {errors.rxIncompatible &&
                isMobile &&
                errors.sph_od &&
                errors.sph_os ? (
                    <IncompatiblePrescription
                        config={props.config}
                        prescriptionObject={compatibleFramesPrescriptionObject}
                        framesUrl={compatibleFramesUrl}
                    />
                ) : null}
                <form id="prescription-form" onSubmit={handleSubmit(onSubmit)}>
                    <div className="prescription-form-table">
                        <div className="prescription-form-row">
                            <div className="prescription-form-col"></div>
                            <div className="prescription-form-col">
                                <div className="prescription-form-row prescription-form-heading">
                                    {props.config.brandName === 'opsm' ? (
                                        <span className="bold">RIGHT (OD)</span>
                                    ) : (
                                        <>
                                            <span className="bold space-right">OD</span> (Right eye)
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="prescription-form-col prescription-form-spacing"></div>
                            <div className="prescription-form-col">
                                <div className="prescription-form-row prescription-form-heading">
                                    {props.config.brandName == 'opsm' ? (
                                        <span className="bold">LEFT (OS)</span>
                                    ) : (
                                        <>
                                            <span className="bold space-right">OS</span> (Left eye)
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="prescription-form-row">
                            <div className="prescription-form-col">
                                <div className="left-label bold">SPH</div>
                            </div>
                            <div className="prescription-form-col">
                                <div
                                    className={
                                        'lc-select' +
                                        (errors.sph_od ? ' error' : '') +
                                        (parseFloat(sphOdVal) * parseFloat(sphOsVal) < 0
                                            ? ' warning'
                                            : '')
                                    }
                                >
                                    <select
                                        tabIndex={
                                            isPrescriptionModalOpen && isPrescriptionFormOpen
                                                ? '-1'
                                                : isPrescriptionFormOpen
                                                ? '0'
                                                : '-1'
                                        }
                                        name="sph_od"
                                        id="sph_od"
                                        ref={register({ required: true })}
                                    >
                                        {utils
                                            .getValuesArray(
                                                constants.prescriptionDefaults.sphMin,
                                                constants.prescriptionDefaults.sphMax,
                                                constants.prescriptionDefaults.sphRes,
                                                '--'
                                            )
                                            .map((e, key) => {
                                                return (
                                                    <option key={key} value={e.value}>
                                                        {e.name}
                                                    </option>
                                                );
                                            })}
                                    </select>
                                </div>
                            </div>
                            <div className="prescription-form-col prescription-form-spacing"></div>
                            <div className="prescription-form-col">
                                <div
                                    className={
                                        'lc-select' +
                                        (errors.sph_os ? ' error' : '') +
                                        (parseFloat(sphOdVal) * parseFloat(sphOsVal) < 0
                                            ? ' warning'
                                            : '')
                                    }
                                >
                                    <select
                                        tabIndex={
                                            isPrescriptionModalOpen && isPrescriptionFormOpen
                                                ? '-1'
                                                : isPrescriptionFormOpen
                                                ? '0'
                                                : '-1'
                                        }
                                        name="sph_os"
                                        id="sph_os"
                                        ref={register({ required: true })}
                                    >
                                        {utils
                                            .getValuesArray(
                                                constants.prescriptionDefaults.sphMin,
                                                constants.prescriptionDefaults.sphMax,
                                                constants.prescriptionDefaults.sphRes,
                                                '--'
                                            )
                                            .map((e, key) => {
                                                return (
                                                    <option key={key} value={e.value}>
                                                        {e.name}
                                                    </option>
                                                );
                                            })}
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className="prescription-form-row prescription-form-spacing"></div>
                        <div className="prescription-form-row">
                            <div className="prescription-form-col">
                                <div className="left-label bold">CYL</div>
                            </div>
                            <div className="prescription-form-col">
                                <div className={'lc-select'}>
                                    <select
                                        tabIndex={
                                            isPrescriptionModalOpen && isPrescriptionFormOpen
                                                ? '-1'
                                                : isPrescriptionFormOpen
                                                ? '0'
                                                : '-1'
                                        }
                                        name="cyl_od"
                                        id="cyl_od"
                                        ref={register({ required: false })}
                                    >
                                        {utils
                                            .getValuesArray(
                                                constants.prescriptionDefaults.cylMin,
                                                constants.prescriptionDefaults.cylMax,
                                                constants.prescriptionDefaults.cylRes,
                                                '--'
                                            )
                                            .map((e, key) => {
                                                return (
                                                    <option key={key} value={e.value}>
                                                        {e.name}
                                                    </option>
                                                );
                                            })}
                                    </select>
                                </div>
                            </div>
                            <div className="prescription-form-col prescription-form-spacing"></div>
                            <div className="prescription-form-col">
                                <div className={'lc-select'}>
                                    <select
                                        tabIndex={
                                            isPrescriptionModalOpen && isPrescriptionFormOpen
                                                ? '-1'
                                                : isPrescriptionFormOpen
                                                ? '0'
                                                : '-1'
                                        }
                                        name="cyl_os"
                                        id="cyl_os"
                                        ref={register({ required: false })}
                                    >
                                        {utils
                                            .getValuesArray(
                                                constants.prescriptionDefaults.cylMin,
                                                constants.prescriptionDefaults.cylMax,
                                                constants.prescriptionDefaults.cylRes,
                                                '--'
                                            )
                                            .map((e, key) => {
                                                return (
                                                    <option key={key} value={e.value}>
                                                        {e.name}
                                                    </option>
                                                );
                                            })}
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className="prescription-form-row">
                            <div className="prescription-form-col"></div>
                            <div className="prescription-form-col align-left">
                                <span className="prescription-error">
                                    {(errors.sph_od || errors.sph_os) &&
                                        !errors.rxIncompatible &&
                                        labels.prescription.missingValueError}
                                </span>
                            </div>
                        </div>
                    </div>
                    <input type="hidden" name="rxIncompatible" ref={register} value="" />
                    {parseFloat(sphOdVal) * parseFloat(sphOsVal) < 0 ? (
                        <div className="prescription-warning no-step">
                            {labels.prescription.positiveNegativeError}
                        </div>
                    ) : (
                        <div
                            tabIndex={
                                isPrescriptionModalOpen || !isPrescriptionFormOpen ? '-1' : '0'
                            }
                            data-element-id="X_X_LensPanel_Type-ValuesHowToRead"
                            className={
                                'prescription-read ' +
                                ((errors.sph_od || errors.sph_os) && !errors.rxIncompatible
                                    ? 'form-error'
                                    : '')
                            }
                            onKeyDown={openPrescriptionModal}
                        >
                            <Image
                                imageClass="read-rx-image"
                                url={
                                    props.config.brandName === 'opsm'
                                        ? props.brandBaseUrl
                                        : baseURLs.projectImage
                                }
                                fileName="read-rx-ico"
                                alt="prescription-read-rx"
                            />
                            <a className="prescription-info" onClick={openPrescriptionModal}>
                                {labels.prescription.readPrescription}
                            </a>
                        </div>
                    )}
                    {(!props.isSingleType && !errors.rxIncompatible) ||
                    !errors.sph_od ||
                    !errors.sph_os ? (
                        <label className="lc-control lc-control--checkbox add-control">
                            {labels.prescription.addLabel}
                            <input
                                type="checkbox"
                                name="addCheck"
                                id="addCheck"
                                ref={register}
                                tabIndex={
                                    isPrescriptionModalOpen || !isPrescriptionFormOpen ? '-1' : '0'
                                }
                            />
                            <div className="lc-control__indicator"></div>
                        </label>
                    ) : null}
                    {!errors.rxIncompatible || !errors.sph_od || !errors.sph_os ? (
                        <div>
                            <GenericButton
                                className="border-button apply-button"
                                type="submit"
                                dataElementId="X_X_LensPanel_Type-ValuesApply"
                                title={labels.prescription.applyButton}
                                tabIndex={
                                    isPrescriptionModalOpen || !isPrescriptionFormOpen ? '-1' : '0'
                                }
                            >
                                {labels.prescription.applyButton}
                            </GenericButton>
                        </div>
                    ) : null}
                </form>
                {!errors.rxIncompatible || !errors.sph_od || !errors.sph_os ? (
                    <GenericButton
                        className="border-button clear-button-prescription"
                        title={labels.prescription.clearButton}
                        tabIndex="0"
                        handleClick={handleClear}
                    >
                        {labels.prescription.clearButton}
                    </GenericButton>
                ) : null}
                {errors.rxIncompatible && errors.sph_od && errors.sph_os ? (
                    <GenericButton
                        className="border-button clear-button-error"
                        title={labels.prescription.clearButton}
                        tabIndex="0"
                        handleClick={handleClear}
                    >
                        {labels.prescription.clearButton}
                    </GenericButton>
                ) : null}
                {errors.rxIncompatible &&
                !isMobile &&
                errors.sph_od &&
                errors.sph_os ? (
                    <IncompatiblePrescription
                        config={props.config}
                        prescriptionObject={compatibleFramesPrescriptionObject}
                        framesUrl={compatibleFramesUrl}
                    />
                ) : null}
                <div className="prescription-ask">{labels.prescription.doctorDetails}</div>
            </div>
        </div>
    );
};
export default SimplePrescriptionForm;
